import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { buildReturnPath } from "../../utils/buildReturnPath";
import { launchConfetti } from "../../utils/confetti";
import "./MatchThreeGame.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const backgroundMusicUrl =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fdraganddrop.mp3?alt=media&token=fbfd52d1-f315-4948-91e2-f2202766d62f";

const defaultBackground =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

const defaultTiles = ["⭐", "💎", "🏴‍☠️", "🧠", "📚", "🎯"];

const randomTile = (tileTypes) =>
  tileTypes[Math.floor(Math.random() * tileTypes.length)];

const createBoard = (size, tileTypes) =>
  Array.from({ length: size }, () =>
    Array.from({ length: size }, () => randomTile(tileTypes))
  );

const MatchThreeGame = ({
  questions = [],
  onComplete,
  user,
  topic,
  placeholderImage,
}) => {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame(); // back to the subject page this game came from
  const { subject } = useParams();

  const size = 6;
  const goalScore = 150;
  const maxMoves = 25;

  const tileTypes = useMemo(() => {
    const fromQuestion = questions?.[0]?.tileTypes;
    return Array.isArray(fromQuestion) && fromQuestion.length >= 3
      ? fromQuestion
      : defaultTiles;
  }, [questions]);

  const [board, setBoard] = useState(() => createBoard(size, tileTypes));
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [movesLeft, setMovesLeft] = useState(maxMoves);
  const [message, setMessage] = useState("Swap two tiles to match 3!");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [completed, setCompleted] = useState(false);

  const musicRef = useRef(null);

  useEffect(() => {
    setBoard(createBoard(size, tileTypes));
    setScore(0);
    setMovesLeft(maxMoves);
    setCompleted(false);
    setMessage("Swap two tiles to match 3!");
  }, [tileTypes]);

  useEffect(() => {
    musicRef.current = new Audio(backgroundMusicUrl);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.3;
    musicRef.current.play().catch(() => {});

    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.currentTime = 0;
      }
    };
  }, []);

  const findMatches = (gameBoard) => {
    const matched = Array.from({ length: size }, () =>
      Array(size).fill(false)
    );

    let count = 0;

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size - 2; c++) {
        const tile = gameBoard[r][c];
        if (
          tile &&
          tile === gameBoard[r][c + 1] &&
          tile === gameBoard[r][c + 2]
        ) {
          matched[r][c] = true;
          matched[r][c + 1] = true;
          matched[r][c + 2] = true;
        }
      }
    }

    for (let c = 0; c < size; c++) {
      for (let r = 0; r < size - 2; r++) {
        const tile = gameBoard[r][c];
        if (
          tile &&
          tile === gameBoard[r + 1][c] &&
          tile === gameBoard[r + 2][c]
        ) {
          matched[r][c] = true;
          matched[r + 1][c] = true;
          matched[r + 2][c] = true;
        }
      }
    }

    matched.forEach((row) => {
      row.forEach((cell) => {
        if (cell) count += 1;
      });
    });

    return { matched, count };
  };

  const dropTiles = (gameBoard) => {
    const nextBoard = gameBoard.map((row) => [...row]);

    for (let c = 0; c < size; c++) {
      const existing = [];

      for (let r = size - 1; r >= 0; r--) {
        if (nextBoard[r][c]) existing.unshift(nextBoard[r][c]);
      }

      const missing = size - existing.length;
      const newTiles = Array.from({ length: missing }, () =>
        randomTile(tileTypes)
      );

      const finalColumn = [...newTiles, ...existing];

      for (let r = 0; r < size; r++) {
        nextBoard[r][c] = finalColumn[r];
      }
    }

    return nextBoard;
  };

  const processMatches = (startingBoard) => {
    let workingBoard = startingBoard.map((row) => [...row]);
    let totalMatched = 0;
    let keepGoing = true;

    while (keepGoing) {
      const { matched, count } = findMatches(workingBoard);

      if (count === 0) {
        keepGoing = false;
      } else {
        totalMatched += count;

        for (let r = 0; r < size; r++) {
          for (let c = 0; c < size; c++) {
            if (matched[r][c]) {
              workingBoard[r][c] = null;
            }
          }
        }

        workingBoard = dropTiles(workingBoard);
      }
    }

    return {
      board: workingBoard,
      points: totalMatched * 10,
      matchedCount: totalMatched,
    };
  };

  const isAdjacent = (a, b) => {
    if (!a || !b) return false;

    const [r1, c1] = a;
    const [r2, c2] = b;

    return (
      (Math.abs(r1 - r2) === 1 && c1 === c2) ||
      (Math.abs(c1 - c2) === 1 && r1 === r2)
    );
  };

  const swap = (gameBoard, first, second) => {
    const nextBoard = gameBoard.map((row) => [...row]);

    const [r1, c1] = first;
    const [r2, c2] = second;

    const temp = nextBoard[r1][c1];
    nextBoard[r1][c1] = nextBoard[r2][c2];
    nextBoard[r2][c2] = temp;

    return nextBoard;
  };

  const finishGame = (finalScore) => {
    if (completed) return;

    setCompleted(true);
    launchConfetti();

    onComplete?.({
      score: finalScore,
      answers: [
        {
          question: topic?.title || "Match 3 Game",
          selectedText: `Score: ${finalScore}`,
          correctText: `Goal: ${goalScore}`,
          correct: finalScore >= goalScore,
        },
      ],
      details: [
        {
          gameType: "match3",
          score: finalScore,
          goalScore,
          movesUsed: maxMoves - movesLeft,
        },
      ],
    });
  };

  const handleTileClick = (row, col) => {
    if (isProcessing || completed) return;

    const currentPick = [row, col];

    if (!selected) {
      setSelected(currentPick);
      setMessage("Now pick a tile next to it!");
      return;
    }

    if (!isAdjacent(selected, currentPick)) {
      setSelected(currentPick);
      setMessage("Pick a tile directly beside it.");
      return;
    }

    setIsProcessing(true);

    const swappedBoard = swap(board, selected, currentPick);
    const result = processMatches(swappedBoard);

    if (result.matchedCount === 0) {
      setBoard(board);
      setMessage("No match. Try another swap!");
      setSelected(null);
      setIsProcessing(false);
      return;
    }

    const nextScore = score + result.points;
    const nextMoves = movesLeft - 1;

    setBoard(result.board);
    setScore(nextScore);
    setMovesLeft(nextMoves);
    setSelected(null);

    if (nextScore >= goalScore) {
      setMessage("Great job! You reached the treasure goal!");
      setTimeout(() => finishGame(nextScore), 700);
      return;
    }

    if (nextMoves <= 0) {
      setMessage("Out of moves! Game complete.");
      setTimeout(() => finishGame(nextScore), 700);
      return;
    }

    setMessage(`Nice match! +${result.points} points!`);
    setIsProcessing(false);
  };

  const handleReturnHome = () => leaveGame();

  return (
    <div
      className="match-three-game-wrapper qk-scene"
    >
      <button
        className="match3-mute-button"
        type="button"
        onClick={() => {
          const nextMuted = !isMuted;
          setIsMuted(nextMuted);
          if (musicRef.current) musicRef.current.muted = nextMuted;
        }}
      >
        {isMuted ? "🔇" : "🔊"}
      </button>

      <button
        type="button"
        className="match3-return-button"
        onClick={handleReturnHome}
      >
        ⬅ Return
      </button>

      <div className="match-three-game">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="SBL Logo"
          className="match3-logo"
        />

        <div className="match3-card">
          <h1>💎 Treasure Match 3</h1>
          <p className="match3-directions">
            Match 3 or more tiles in a row. Reach {goalScore} points before your
            moves run out!
          </p>

          <div className="match3-stats">
            <span>⭐ Score: {score}</span>
            <span>🎯 Goal: {goalScore}</span>
            <span>🕹️ Moves: {movesLeft}</span>
          </div>

          <p className="match3-message">{message}</p>

          <div className="board">
            {board.map((rowItems, r) =>
              rowItems.map((tile, c) => {
                const isSelected =
                  selected?.[0] === r && selected?.[1] === c;

                return (
                  <button
                    key={`${r}-${c}`}
                    type="button"
                    className={`tile ${isSelected ? "selected" : ""}`}
                    onClick={() => handleTileClick(r, c)}
                  >
                    {tile}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchThreeGame;